/**
 * Типы фото-полей карточки тренда — доступ к общему определению (types.json).
 *
 * МАСТЕР — shared/photo-slots; этот файл и types.json копирует в frontend/src/photo-slots и admin/src/photo-slots
 * скрипт scripts/sync_prompt_vars.sh (CI и тесты ловят расхождение). Править только мастер. Модуль не импортирует
 * ничего из приложений — он одинаков в обоих.
 *
 * Тип поля задаёт админ в конструкторе промптов (photo_slots[i].type); по нему сайт рисует значок в углу плитки,
 * лист «?» (что загрузить, с образцами) и, если админ включил на поле проверку (photo_slots[i].check), после загрузки
 * спрашивает сервер (services/photo_check) и пишет ответ внутри плитки. Поле без типа — плитка как раньше.
 */
import spec from './types.json'

export type PhotoSlotType = 'face' | 'full_body' | 'clothing' | 'object' | 'background'
/** Что умеет проверить сервер (services/photo_check): ключ проверки у типа поля. */
export type PhotoCheckKind = 'face' | 'full_body' | 'no_people'

export interface PhotoSlotTypeDef {
	key: PhotoSlotType
	/** Имя в списке конструктора. */
	label: string
	/** Подпись плитки по умолчанию, если админ не дал свою. */
	tile: string
	/** Значок в углу плитки — path в сетке 24×24 (stroke, без заливки). */
	icon: string
	/** Проверка после загрузки, которую можно включить на поле; null — для типа проверки нет. */
	check: PhotoCheckKind | null
	check_label: string | null
	help: {
		/** Одной строкой: что загрузить. */
		what: string
		rules: string[]
		/** Подписи образцов «подходит» / «не подходит». */
		good: string
		bad: string
	}
}

/** Поле фото в карточке (photo_slots[i]) — как его хранит конструктор и читает сайт. */
export interface CardPhotoSlot {
	label?: string
	hint?: string
	type?: PhotoSlotType
	/** Проверять фото после загрузки (только у типов с check); включает админ на конкретное поле. */
	check?: boolean
}

interface PhotoSlotsSpec {
	version: number
	types: PhotoSlotTypeDef[]
	checks: Record<PhotoCheckKind, string>
}

export const PHOTO_SLOTS_SPEC = spec as unknown as PhotoSlotsSpec

export function photoSlotTypes(): PhotoSlotTypeDef[] {
	return PHOTO_SLOTS_SPEC.types
}

/** Определение типа поля; неизвестный или пустой тип — null (плитка без значка и листа). */
export function photoSlotType(key: string | null | undefined): PhotoSlotTypeDef | null {
	if (!key) return null
	return PHOTO_SLOTS_SPEC.types.find((t) => t.key === key) ?? null
}

/** Проверка, которую запустит сайт для поля: тип с проверкой и включённый флаг; иначе null. */
export function photoSlotCheck(slot: CardPhotoSlot | null | undefined): PhotoCheckKind | null {
	if (!slot?.check) return null
	return photoSlotType(slot.type)?.check ?? null
}

/** Подпись плитки: своя от админа, иначе подпись типа, иначе «Фото N». */
export function photoSlotLabel(slot: CardPhotoSlot | null | undefined, n: number): string {
	return slot?.label || photoSlotType(slot?.type)?.tile || `Фото ${n}`
}

/**
 * Сколько именованных фото-полей вмещает модель (одно правило для сайта и конструктора): maxImages модели;
 * у Seedance 2.x поля уходят референсами — не меньше девяти (режим «Референсы»). Модель без мульти-фото — одно.
 */
export function photoSlotCapacity(model: { id?: string; backendModel?: string; maxImages?: number | null } | null | undefined): number {
	const max = model?.maxImages ?? 1
	const id = `${model?.backendModel || ''} ${model?.id || ''}`.toLowerCase()
	return id.includes('seedance-2') ? Math.max(max, 9) : max
}
